import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Loader2,
  RefreshCw,
  Filter,
  CheckCircle2,
  XCircle,
  ChevronRight,
  AlertTriangle,
  RotateCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { fmtServerDateTime as fmtTime } from './utils/datetime';

const PROVIDER_CLS = {
  gmail: 'bg-red-500/15 text-red-300 border-red-500/30',
  smtp:  'bg-blue-500/15 text-blue-300 border-blue-500/30',
  ses:   'bg-orange-500/15 text-orange-300 border-orange-500/30',
};

export function AdminEmailLogsPage() {
  const { admin, loading } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [fetching, setFetching] = useState(true);
  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const [scope, setScope] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [retrying, setRetrying] = useState(null);

  const refresh = useCallback(async () => {
    setFetching(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (status) params.set('status', status);
      if (provider) params.set('provider', provider);
      if (scope) params.set('scope', scope);
      if (search.trim()) params.set('search', search.trim());
      const r = await adminApi.get(`/admin/email-logs?${params.toString()}`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch {
      toast.error('Could not load email logs');
    } finally {
      setFetching(false);
    }
  }, [page, pageSize, status, provider, scope, search]);

  useEffect(() => { if (admin) refresh(); }, [admin, refresh]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const retry = async (log) => {
    setRetrying(log.id);
    try {
      await adminApi.post(`/admin/email-logs/${log.id}/retry`);
      toast.success('Retry dispatched');
      await refresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Retry failed');
    } finally {
      setRetrying(null);
    }
  };

  // Quick delivery stats for the filtered view
  const sentCount = items.filter((i) => i.status === 'sent').length;
  const failedCount = items.filter((i) => i.status === 'failed').length;

  return (
    <div
      data-testid="admin-email-logs-page"
      className="min-h-screen"
      style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/admin/email-integrations" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800" data-testid="email-logs-back">
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <Mail className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold">Email Delivery Logs</div>
            <div className="text-xs text-slate-400">Every outbound email attempt — system (OTP), customer (license / invite), tests</div>
          </div>
          <Button onClick={refresh} disabled={fetching} variant="outline" size="sm" data-testid="email-logs-refresh" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${fetching ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-4">
        {/* Stat chips */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-900/70 border border-slate-800 rounded-xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Total (this page)</div>
            <div className="text-xl font-bold text-white">{items.length}</div>
          </div>
          <div className="bg-slate-900/70 border border-emerald-500/20 rounded-xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-emerald-300">Sent</div>
            <div className="text-xl font-bold text-emerald-300 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> {sentCount}
            </div>
          </div>
          <div className="bg-slate-900/70 border border-red-500/20 rounded-xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-red-300">Failed</div>
            <div className="text-xl font-bold text-red-300 flex items-center gap-2">
              <XCircle className="w-4 h-4" /> {failedCount}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-slate-400 uppercase tracking-wider">
            <Filter className="w-3.5 h-3.5" /> Filters
          </div>
          <select
            data-testid="email-logs-status-filter"
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="bg-slate-800/50 border border-slate-700 text-white text-xs rounded-md px-2 py-1.5"
          >
            <option value="">All statuses</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
          <select
            data-testid="email-logs-provider-filter"
            value={provider}
            onChange={(e) => { setProvider(e.target.value); setPage(1); }}
            className="bg-slate-800/50 border border-slate-700 text-white text-xs rounded-md px-2 py-1.5"
          >
            <option value="">All providers</option>
            <option value="gmail">Gmail</option>
            <option value="smtp">Custom SMTP</option>
            <option value="ses">AWS SES</option>
          </select>
          <select
            data-testid="email-logs-scope-filter"
            value={scope}
            onChange={(e) => { setScope(e.target.value); setPage(1); }}
            className="bg-slate-800/50 border border-slate-700 text-white text-xs rounded-md px-2 py-1.5"
          >
            <option value="">All scopes</option>
            <option value="system">System (OTP)</option>
            <option value="customer">Customer (License / Invite)</option>
          </select>
          <Input
            data-testid="email-logs-search"
            placeholder="Recipient / code / subject…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); refresh(); }}}
            className="bg-slate-800/50 border-slate-700 text-white text-sm h-9 max-w-xs"
          />
          <span className="text-xs text-slate-500 ml-auto">
            {total.toLocaleString('en-IN')} event{total === 1 ? '' : 's'}
          </span>
        </div>

        {/* Table */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-xl overflow-hidden">
          {fetching ? (
            <div className="p-10 flex items-center justify-center text-slate-400 gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">No email log entries match your filters.</div>
          ) : (
            <table className="w-full text-sm" data-testid="email-logs-table">
              <thead className="bg-slate-950/60">
                <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-2 text-left w-44">Time</th>
                  <th className="px-4 py-2 text-left w-20">Status</th>
                  <th className="px-4 py-2 text-left w-24">Provider</th>
                  <th className="px-4 py-2 text-left">Recipient</th>
                  <th className="px-4 py-2 text-left">Subject / License</th>
                  <th className="px-4 py-2 text-right w-20">Retries</th>
                  <th className="px-4 py-2 w-28"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {items.map((log) => {
                  const isSent = log.status === 'sent';
                  const isExpanded = expanded === log.id;
                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        data-testid={`email-log-row-${log.id}`}
                        className={`cursor-pointer ${isSent ? 'hover:bg-slate-800/30' : 'bg-red-500/5 hover:bg-red-500/10'}`}
                        onClick={() => setExpanded(isExpanded ? null : log.id)}
                      >
                        <td className="px-4 py-2.5 text-slate-400 font-mono text-xs">{fmtTime(log.created_at)}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${
                            isSent
                              ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                              : 'bg-red-500/15 text-red-300 border-red-500/30'
                          }`}>
                            {isSent ? <CheckCircle2 className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
                            {log.status}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${PROVIDER_CLS[log.provider] || 'bg-slate-700 text-slate-300'}`}>
                            {log.provider}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-200 truncate max-w-xs">{log.recipient}</td>
                        <td className="px-4 py-2.5 text-xs min-w-0">
                          <div className="text-slate-300 truncate">{log.subject || '—'}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            {log.license_code && (
                              <span className="font-mono text-[10px] text-amber-300 truncate">{log.license_code}</span>
                            )}
                            {log.scope && (
                              <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-slate-800 text-slate-400">{log.scope}</span>
                            )}
                            {log.purpose && (
                              <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-slate-800 text-slate-400">{log.purpose}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-right text-xs">
                          {(log.retry_count || 0) > 0 ? (
                            <span className="inline-flex items-center gap-1 font-mono text-amber-300">
                              <RotateCw className="w-3 h-3" />{log.retry_count}
                            </span>
                          ) : (
                            <span className="text-slate-600">—</span>
                          )}
                        </td>
                        <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1 justify-end">
                            {log.retryable && (
                              <button
                                type="button"
                                onClick={() => retry(log)}
                                disabled={retrying === log.id}
                                data-testid={`email-log-retry-${log.id}`}
                                title="Retry delivery"
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25 disabled:opacity-50"
                              >
                                {retrying === log.id
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <RotateCw className="w-3 h-3" />}
                                Retry
                              </button>
                            )}
                            <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className={isSent ? 'bg-slate-950/40' : 'bg-red-950/20'} data-testid={`email-log-details-${log.id}`}>
                          <td colSpan={6} className="px-6 py-3">
                            {log.error && (
                              <div className="mb-3 rounded-md border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200">
                                <div className="text-[10px] uppercase tracking-wider text-red-300 font-bold mb-1">Error</div>
                                <div className="font-mono break-all">{log.error}</div>
                              </div>
                            )}
                            {log.last_retry_error && (
                              <div className="mb-3 rounded-md border border-orange-500/40 bg-orange-500/10 p-3 text-xs text-orange-200">
                                <div className="text-[10px] uppercase tracking-wider text-orange-300 font-bold mb-1">Last retry error</div>
                                <div className="font-mono break-all">{log.last_retry_error}</div>
                              </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                              {log.license_id && (
                                <div className="flex gap-2"><span className="text-slate-500 w-24">license_id</span><span className="font-mono text-slate-300 truncate">{log.license_id}</span></div>
                              )}
                              {log.actor_id && (
                                <div className="flex gap-2"><span className="text-slate-500 w-24">actor_id</span><span className="font-mono text-slate-300 truncate">{log.actor_id}</span></div>
                              )}
                              <div className="flex gap-2"><span className="text-slate-500 w-24">actor_type</span><span className="text-slate-300">{log.actor_type}</span></div>
                            </div>
                            {log.details && Object.keys(log.details).length > 0 && (
                              <div className="mt-3">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">details</div>
                                <pre className="bg-slate-950 border border-slate-800 rounded-md p-3 text-[11px] text-emerald-200 overflow-auto max-h-60">{JSON.stringify(log.details, null, 2)}</pre>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {total > 0 && (
          <div className="flex items-center justify-between text-xs text-slate-400">
            <div>Page {page} of {totalPages}</div>
            <div className="flex gap-2">
              <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || fetching} variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Prev</Button>
              <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || fetching} variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Next</Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
